import React, { useEffect } from 'react'
import cn from 'classnames'
import * as yup from 'yup'
import { useFormikContext, Field } from 'formik'
import { Checkbox, Link, Station, VehicleCount } from 'components'
import { Vehicle, NewBooking } from 'api/types'
import { formatPrice, formatDateTime, formatKilometers } from 'utils/format'
import { calculatePrice } from './priceUtils'
import classes from './SummaryFields.module.scss'
import { useTranslation } from 'react-i18next'
import { bookingDurationInDays } from 'utils/bookingDateUtils'

export const validationSchema = yup.object({
  acceptTerms: yup
    .bool()
    .oneOf(
      [true],
      'pages.bookings.bookingForm.summaryFields.acceptTerms.required'
    ),
})

interface SummaryFieldsProps {
  vehicles: Vehicle[]
}

export const SummaryFields: React.FunctionComponent<SummaryFieldsProps> = ({
  vehicles,
}) => {
  const { setFieldValue, values: booking } = useFormikContext<NewBooking>()
  const vehicle = vehicles.find((v) => v.id === booking.vehicleId)
  const days = bookingDurationInDays(booking.startDate, booking.endDate)
  /* If we are editing a booking, make sure to also accept terms */
  useEffect(() => {
    if (booking.acceptTerms === undefined) {
      setFieldValue('acceptTerms', false)
    }
  }, [booking, setFieldValue])

  const { t } = useTranslation()
  return (
    <div>
      <table className={cn('wb-table', 'wb-table--striped')}>
        <tbody>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.startDateTime')}
            </th>
            <td>{formatDateTime(booking.startDate)}</td>
          </tr>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.endDateTime')}
            </th>
            <td>{formatDateTime(booking.endDate)}</td>
          </tr>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.pickUpStation')}
            </th>
            <td>
              <Station stationId={booking.pickUpStationId} />
            </td>
          </tr>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.vehicle')}
            </th>
            <td>
              <VehicleCount
                count={booking.vehicleCount}
                vehicleGroup={vehicle?.group}
              />
            </td>
          </tr>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.freeKilometers')}
            </th>
            <td>
              {vehicle && formatKilometers(vehicle.includedKilometers / days)}
            </td>
          </tr>
          <tr>
            <th scope="row">
              {t('pages.bookings.bookingForm.summaryFields.expectedPrice')}
            </th>
            <td>
              {vehicle &&
                formatPrice(calculatePrice(vehicle, booking.vehicleCount))}
            </td>
          </tr>
        </tbody>
      </table>

      <p className={classes.paragraph}>
        {t('pages.bookings.bookingForm.summaryFields.summaryInfo')}
      </p>

      <Field
        component={Checkbox}
        id="acceptTerms"
        name="acceptTerms"
        label={
          <>
            {t('pages.bookings.bookingForm.summaryFields.acceptTerms.iAccept')}{' '}
            <Link
              variant="inline"
              to={
                process.env.PUBLIC_URL +
                '/250918_AMB_2025_final.pdf'
              }
              target="_blank"
              className={classes.links}
            >
              {t(
                'pages.bookings.bookingForm.summaryFields.acceptTerms.rentTerms'
              )}
            </Link>
          </>
        }
        checked={booking.acceptTerms || false}
        onChange={() => setFieldValue('acceptTerms', !booking.acceptTerms)}
        className={classes.checkbox}
      />

      <p className={classes.dataPrivacyNote}>
        {t('pages.bookings.bookingForm.summaryFields.dataPrivacyNote.text')}
        <Link
          variant="inline"
          to={process.env.PUBLIC_URL + '/Datenschutzhinweise_MBAM_2025_04.pdf'}
          target="_blank"
        >
          {t(
            'pages.bookings.bookingForm.summaryFields.dataPrivacyNote.linkText'
          )}
        </Link>
      </p>
    </div>
  )
}
